import React, { useEffect, useState } from 'react';
import Yup from '~yup';
import { useFormik } from 'formik';

import Text from '~components/atoms/Text';
import ActionButton from '~components/atoms/ActionButton';
import LabelledInput from '~components/atoms/LabelledInput';
import LabelledDropdown from '~components/atoms/LabelledDropdown';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { BranchStatusEnum, CustomerAccFundTypeEnum, CustomerAccTypeEnum, GoldTypeEnum, MetalTypeEnum, SilverTypeEnum } from '~utils/enums';

import { TDropdownOption } from '~types/components/labelledDropdown';
import UtilFunctions from '~utils/functions';

import * as FormStyles from './_createGoldConversionForm.module.scss';
import { TGoldConversionFiltersRedux, TInventoryFilter } from '~types/feat/gold';

const CreateGoldConversionForm = () => {
    const dispatch = useAppDispatch();

    const { Misc: { fixedDecimal } } = UtilFunctions;

    const searchCustomerIsLoading = useAppSelector(state => state.customer.loadings.getAllCustomers);
    const createInventoryIsLoading = useAppSelector((state) => state.gold.loadings.createInventory);

    const branchesList = useAppSelector((state) => state.branch.allBranches);
    const regionList = useAppSelector((state) => state.branch.allRegions);
    const inventoryList = useAppSelector((state) => state.gold.allInventories);
    const serialNumberList = useAppSelector((state) => state.gold.allSerialNumbers);
    const customerList = useAppSelector((state) => state.customer.allCustomers);

    const customerDetails = useAppSelector((state) => state.customer.customerDetails);
    const goldConversionAmount = useAppSelector((state) => state.gold.goldConversion.goldConversionAmount);

    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);

    const inventoryFilterParams = useAppSelector((state) => state.gold.params.inventoryFilterParams);

    const customerInventory = useAppSelector((state) => state.customer.customerInventory);

    const [disableCustomerSearch, setDisableCustomerSearch] = useState(false);
    const [gettingCustomers, setGettingCustomers] = useState(false);
    const [name, setName] = useState('');
    const [selectedBranchCode, setSelectedBranchCode] = useState('');
    const [targetProductName, setTargetProductName] = useState('');
    const [targetProductAmount, setTargetProductAmount] = useState(0);

    const validationSchema = Yup.object().shape({
        name: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().required('Customer name is required'),
        idNo: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().required('Customer ID is required'),
        CIFNo: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().required('Customer CIF is required'),
        branchName: Yup.string().
            matchNoLeadingAndTrailingWhiteSpaces()
            .matchMaxOneWhiteSpace()
            .test('check-branch-name',
                'Selected branch does not exist',
                function (value) {
                    if (!value) return false;
                    return branchOptions.some((branch) => branch.label === value);
                }
            )
            .required('Branch name is required'),
        branchCode: Yup.string().required('Branch code is required'),
        region: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().required('Region is required'),
        originProductId: Yup.string().required('Select origin product'),
        availablePaperGold: Yup.string(),
        availablePaperSilver: Yup.string(),
        targetProductId: Yup.string().required('Select a product'),
        quantity: Yup.number()
            .test(
                'is-valid-quantity',
                'Quantity must be at least 1',
                function (value) {
                    if (!value) return false;
                    return !isNaN(value);
                }
            )
            .test('no-exponential-notation',
                'Quantity must be a whole number (exponential notation is not allowed)',
                function (value) {
                    const original = this.originalValue;
                    if (original === undefined || original === null || original === '') return true;
                    return !/e/i.test(String(original));
                }
            )
            .test('is-within-balance',
                'Quantity must not exceed paper gold balance',
                function (value) {
                    const { metalType } = this.parent;

                    const isSilver = Number(metalType) === MetalTypeEnum.Silver;

                    const availableBalance = goldConversionAmount;

                    const metalName = isSilver ? 'silver' : 'gold';

                    if (!availableBalance) {
                        return this.createError({
                            message: `Quantity must not exceed ${metalName} balance 0`,
                        });
                    }

                    if (isNaN(Number(availableBalance))) {
                        return false;
                    }

                    if (!value) return false;

                    if (value > Number(availableBalance)) {
                        return this.createError({
                            message: `Quantity must not exceed ${metalName} balance ${availableBalance}`,
                        });
                    }

                    return true;
                }
            )
            .test(
                'is-within-inventory-balance',
                function (value) {
                    const { targetProductId } = this.parent;

                    const product = inventoryList.find(
                        (p) => p.productId === Number(targetProductId)
                    );

                    const isSilver = product?.metalType === MetalTypeEnum.Silver;

                    const availableInventory = targetProductAmount;

                    const metalName = isSilver ? 'silver' : 'gold';

                    if (!availableInventory) {
                        return this.createError({
                            message: 'Quantity must not exceed inventory balance 0',
                        });
                    }

                    if (isNaN(Number(availableInventory))) {
                        return false;
                    }

                    if (!value) return false;

                    if (value > Number(availableInventory)) {
                        return this.createError({
                            message: `Quantity must not exceed ${metalName} inventory balance ${availableInventory}`,
                        });
                    }

                    return true;
                }
            )
            .typeError('Quantity must be a number')
            .positive('Quantity must be positive')
            .max(1000000, 'Amount must not exceed 1000000 grams')
            .required('Quantity is required'),
        serialNo: Yup.string()
            .matchNoLeadingAndTrailingWhiteSpaces()
            .matchNoLeadingAndTrailingCommas()
            .matchZeroWhiteSpace()
            .when('targetProductId', {
                is: (val: string) => {
                    const product = inventoryList.find((p) => p.productId === Number(val));
                    return product?.goldType === GoldTypeEnum.PhysicalGold
                        || product?.silverType === SilverTypeEnum.PhysicalSilver;
                },
                then: (schema) => schema.test('serial-numbers-count',
                    function (value) {
                        const { quantity } = this.parent;

                        if (!value) return false;
                        if (quantity === 0) return true;
                        const serialNumbers = value.split(',').map(s => s.trim()).filter(s => s.length > 0);

                        if (serialNumbers.length !== quantity) {
                            return this.createError({
                                message: `Must provide exactly ${quantity} serial number${quantity !== 1 ? 's' : ''}`,
                            });
                        }
                        return serialNumbers.length === quantity;
                    }
                ).test('duplicate-serial-numbers',
                    'The provided serial numbers are not unique',
                    function (value) {
                        if (!value) return false;
                        const valuesArray = value.split(',').map(s => s.trim()).filter(s => s.length > 0);
                        return valuesArray.length === new Set(valuesArray).size;
                    }
                ),
                otherwise: (schema) => schema.test('serial-numbers-count',
                    'Must provide exactly 1 serial number',
                    function (value) {
                        if (!value) return false;
                        const serialNumbers = value.split(',').map(s => s.trim()).filter(s => s.length > 0);
                        return serialNumbers.length === 1;
                    }
                ),
            }),
        salesPersonnelName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace(),
        referralName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace(),
        metalType: Yup.number()
            .required('Metal Type is required')
            .min(1, 'Metal Type is required'),
    });

    const CreateInventoryInitialValues = {
        customerId: 0,
        name: '',
        idNo: '',
        CIFNo: '',
        branchName: '',
        branchCode: '',
        region: '',
        originProductId: '',
        availablePaperGold: '',
        availablePaperSilver: '',
        targetProductId: '',
        quantity: '',
        serialNo: '',
        salesPersonnelName: '',
        referralName: '',
        metalType: '',
    };

    const formik = useFormik({
        initialValues: CreateInventoryInitialValues,
        validationSchema: validationSchema,
        enableReinitialize: true, // Add this to update validation when serialNumberRequired changes
        onSubmit: values => {
            const {
                quantity,
                originProductId,
                targetProductId,
                customerId,
                serialNo,
                branchCode: branchCodeValue,
                salesPersonnelName,
                referralName,
                metalType,
            } = values;

            dispatch(combinedActions.goldCreateGoldConversionAttempt({
                amount: Number(quantity),
                originProductId: Number(originProductId),
                targetProductId: Number(targetProductId),
                customerId: customerId,
                serialNo: serialNo.replace(/\s+/g, '').split(','),
                branchCode: branchCodeValue,
                salesPersonnelName,
                referralName,
                metalType: Number(metalType),
            }));
        },
    });

    const customerOptions = customerList.map((customer) => ({
        label: `${customer.name} - ${customer.idNo}  (${CustomerAccFundTypeEnum[customer.fundType]}) (${CustomerAccTypeEnum[customer.accountType]})${customer.accountType === CustomerAccTypeEnum.Joint ? (' - ' + customer.childName) : ''}`,
        value: customer.customerId,
    }));

    const branchOptions = branchesList.filter(branch => branch.status === BranchStatusEnum.Active).map((branch) => ({
        label: branch.branchName,
        value: branch.branchCode,
    }));

    const regionOptions = regionList.map((region) => ({
        label: region.regionName,
        value: region.regionName,
    }));

    const physicalGoldSilverProducts = inventoryList
        .filter(item =>
            (item.goldType === GoldTypeEnum.PhysicalGold || item.silverType === SilverTypeEnum.PhysicalSilver)
            && Number(item.fundType) === Number(customerDetails.fundType)
        )
        .map(inventory => ({
            label: inventory.productName,
            value: inventory.productId,
        }));

    useEffect(() => {
        // Note: Not batching this setFieldValue with branchName's onChange setFieldValue because of a race condition
        // the branchName validation will fire first before the update is done, rendering an error for the branchName field
        const newBranch = branchesList.find(branch => branch.branchName === formik.values.branchName);

        if (newBranch) {
            formik.setFieldValue('branchCode', newBranch?.branchCode);
        } else {
            formik.setFieldValue('branchCode', '');
        }
    }, [formik.values.branchName]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (name && !disableCustomerSearch) {
            setGettingCustomers(true);

            // debounce customer name input
            timeout = setTimeout(() => {
                dispatch(combinedActions.customerGetAllCustomersAttempt({ searchQuery: name }));
            }, 1000);
        } else setGettingCustomers(false);

        setDisableCustomerSearch(false);

        return () => {
            setGettingCustomers(false);
            clearTimeout(timeout);
        };
    }, [name]);

    useEffect(() => {
        // to set loading state (debouncing causes loading state to also delay hence we use a local loading state)
        if (!searchCustomerIsLoading) {
            setGettingCustomers(false);
        }
    }, [searchCustomerIsLoading]);

    useEffect(() => {
        if (selectedBranchCode) {
            const currentMetalType = formik.values.metalType
                ? Number(formik.values.metalType) as MetalTypeEnum
                : MetalTypeEnum.Gold;

            dispatch(combinedActions.goldSetInventoryFilterParams({
                ...inventoryFilterParams,
                branchCode: selectedBranchCode,
                metalType: currentMetalType,
                goldType: currentMetalType === MetalTypeEnum.Gold ? GoldTypeEnum.PhysicalGold : undefined,
                silverType: currentMetalType === MetalTypeEnum.Silver ? SilverTypeEnum.PhysicalSilver : undefined,
            }));
            dispatch(combinedActions.goldGetAllInventoriesAttempt());

            formik.setValues({
                ...formik.values,
                branchCode: selectedBranchCode,
                targetProductId: '',
            });
        }
    }, [selectedBranchCode]);

    useEffect(() => {
        if (customerDetails){
            formik.setFieldValue('availablePaperGold', customerDetails.paperGoldBalance);
            formik.setFieldValue('availablePaperSilver', customerDetails.paperSilverBalance);
        }
    }, [customerDetails]);

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess) {
            const currentBranch = branchesList.find(branch => branch.branchCode === branchCode);

            if (!currentBranch) return;

            const branchRegion = regionList.find(region => region.regionId === currentBranch.regionId);

            formik.setFieldValue('branchName', currentBranch.branchName);
            formik.setFieldValue('branchCode', currentBranch.branchCode);
            formik.setFieldValue('region', branchRegion?.regionName || '');

            setSelectedBranchCode(currentBranch.branchCode);
        }
    }, [branchesList, regionList, allBranchAccess, branchCode]);

    useEffect(() => {
        if (customerInventory?.length) {
            const currentMetalType = formik.values.metalType ? Number(formik.values.metalType) as MetalTypeEnum : MetalTypeEnum.Gold;

            const paperProduct = customerInventory.find(item =>
                currentMetalType === MetalTypeEnum.Silver
                    ? item.silverType === SilverTypeEnum.PaperSilver
                    : item.goldType === GoldTypeEnum.PaperGold
            );

            formik.setFieldValue('originProductId', paperProduct?.productId?.toString() || '');
        }
    }, [customerInventory]);

    const currentSerialNumberList = React.useMemo(
        () =>
            serialNumberList.find(
                (group) => group[0]?.productId === Number(formik.values.targetProductId)
            ) ?? [],
        [serialNumberList, formik.values.targetProductId],
    );

    const setProductSerialNumber = (quantity: number, selectedProduct: TInventoryFilter) => {
        if (selectedProduct && quantity) {
            const availableAmount = selectedProduct.amount;

            if (quantity > 0 && quantity <= availableAmount) {
                if (quantity === availableAmount) {
                    const serialNumbers = currentSerialNumberList.map((serialNumber) => serialNumber.serialNo);
                    formik.setFieldValue('serialNo', serialNumbers.length > 1 ? serialNumbers.join(',') : serialNumbers[0]);
                    return;
                }

                const serialNumbers = currentSerialNumberList.slice(0, quantity).map((serialNumber) => serialNumber.serialNo);

                formik.setFieldValue('serialNo', serialNumbers.length > 1 ? serialNumbers.join(',') : (serialNumbers[0] || ''));
                return;
            } else {
                formik.setFieldValue('serialNo', '');
                return;
            }
        } else {
            formik.setFieldValue('serialNo', '');
            return;
        }
    };

    const handleCustomerSelected = (customerId: number) => {
        dispatch(combinedActions.customerGetCustomerDetailsAttempt({ customerId }));
        dispatch(combinedActions.customerGetCustomerInventoryAttempt({ customerId }));
        dispatch(combinedActions.goldResetGoldConversionAmount());
    };

    const handleOnSearchCustomer = (event: React.ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value);

        const customer = customerList.find((customerOption) => customerOption.idNo === event.target.value);

        if (!customer) return;

        formik.setValues({
            ...CreateInventoryInitialValues,
            branchName: formik.values.branchName,
            branchCode: formik.values.branchCode,
            region: formik.values.region,
            customerId: Number(customer.customerId),
            name: customer.name,
            idNo: customer.idNo,
            CIFNo: customer.CIFNo,

        });
    };

    const handleOnSelectCustomer = (option: TDropdownOption) => {
        const customer = customerList.find((customerOption) => customerOption.customerId === option.value);

        if (!customer) return;

        setDisableCustomerSearch(true);

        setTargetProductName('');

        handleCustomerSelected(customer.customerId);

        formik.setValues({
            ...CreateInventoryInitialValues,
            branchName: formik.values.branchName,
            branchCode: formik.values.branchCode,
            region: formik.values.region,
            customerId: Number(customer.customerId),
            name: customer.name,
            idNo: customer.idNo,
            CIFNo: customer.CIFNo,
            originProductId: '', // resolved by the customerInventory effect once it loads
        });
    };

    const handleOnSearchBranch = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({ target: { name: 'branchName', value: event.target.value } });

        const branch = branchesList.find((branchOption) => branchOption.branchName === event.target.value);
        const branchRegion = regionList.find((regionOption) => regionOption.regionId === branch?.regionId);

        if (!branch) {
            dispatch(combinedActions.goldClearInventoryFilterParams());
            formik.handleChange({ target: { name: 'region', value: '' } });
            formik.handleChange({ target: { name: 'branchCode', value: '' } });
            setSelectedBranchCode('');
            return;
        }

        if (branch) {
            setSelectedBranchCode(branch.branchCode);
            formik.handleChange({ target: { name: 'branchCode', value: branch.branchCode } });
        }

        if (branchRegion) {
            formik.handleChange({ target: { name: 'region', value: branchRegion.regionName } });
        }
    };

    const handleOnSelectBranch = (option: TDropdownOption) => {
        const branch = branchesList.find((branchOption) => branchOption.branchCode === option.value);
        const branchRegion = regionList.find((regionOption) => regionOption.regionId === branch?.regionId);

        setSelectedBranchCode(option.value as string);
        formik.handleChange({ target: { name: 'branchCode', value: option.value } });

        if (branchRegion) {
            formik.handleChange({ target: { name: 'region', value: branchRegion.regionName } });
        }
    };

    const handleOnSearchConvertToProduct = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTargetProductName(event.target.value);

        const product = inventoryList.find((productOption) => productOption.productName === event.target.value);

        if (!product) {
            formik.setFieldValue('targetProductId', '');
            return;
        }

        formik.setFieldValue('targetProductId', product.productId);
        dispatch(combinedActions.goldSetSerialNumberFilterParams({ branchCode: formik.values.branchCode, productId: product.productId }));
        dispatch(combinedActions.goldGetAllSerialNumbersAttempt());
    };

    const handleOnSelectConvertToProduct = (option: TDropdownOption) => {
        setTargetProductName(option.label);

        const product = inventoryList.find((productOption) => productOption.productId === option.value);

        if (!product) {
            formik.setFieldValue('targetProductId', '');
            return;
        }

        const currentMetalType = formik.values.metalType ? Number(formik.values.metalType) as MetalTypeEnum : MetalTypeEnum.Gold;
        const relevantBalance = currentMetalType === MetalTypeEnum.Silver
            ? customerDetails.paperSilverBalance
            : customerDetails.paperGoldBalance;

        formik.setFieldValue('targetProductId', product.productId);
        // dispatch(combinedActions.goldGetGoldConversionAmountAttempt({
        //     productId: product.productId,
        //     amount: Number(fixedDecimal(relevantBalance, 3)),
        // }));
        // dispatch(combinedActions.goldSetSerialNumberFilterParams({ branchCode: branchCode, productId: product.productId }));
        dispatch(combinedActions.goldGetGoldConversionAmountAttempt({ productId: product.productId, amount: Number(fixedDecimal(customerDetails.paperGoldBalance, 3)) }));
        dispatch(combinedActions.goldSetSerialNumberFilterParams({ branchCode: formik.values.branchCode, productId: product.productId }));
        dispatch(combinedActions.goldGetAllSerialNumbersAttempt());
    };

    const computeSerialNo = (quantity: number, selectedProduct: TInventoryFilter): string => {
        if (!selectedProduct || !quantity) return '';

        const availableAmount = selectedProduct.amount;

        if (quantity <= 0 || quantity > availableAmount) return '';

        const serials = quantity === availableAmount
            ? currentSerialNumberList
            : currentSerialNumberList.slice(0, quantity);

        const serialNumbers = serials.map((s) => s.serialNo);
        return serialNumbers.length > 1 ? serialNumbers.join(',') : (serialNumbers[0] || '');
    };

    const handleOnChangeQuantity = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        const selectedProduct = inventoryList.find(
            (productOption) => productOption.productId === Number(formik.values.targetProductId)
        );

        if (selectedProduct) {
            setTargetProductAmount(selectedProduct.amount);
        }

        formik.setValues({
            ...formik.values,
            quantity: value,
            serialNo: selectedProduct ? computeSerialNo(Number(value), selectedProduct) : formik.values.serialNo,
        });
    };

    const handleQuantityOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        formik.handleBlur(event);
    };

    const handleOnSearchRegion = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({ target: { name: 'region', value: event.target.value } });
    };

    const handleOnSelectRegion = (option: TDropdownOption) => {
        formik.handleChange({ target: { name: 'region', value: option.value } });
    };

    const metalTypeOptions = [
        { label: 'Gold', value: String(MetalTypeEnum.Gold) },
        { label: 'Silver', value: String(MetalTypeEnum.Silver) },
    ];

    return (
        <form
            onSubmit={formik.handleSubmit}
            className={FormStyles.container}
        >
            <div className={FormStyles['form-container']}>
                <LabelledDropdown
                    isFilter
                    label='Search for customer name or ID* : '
                    options={customerOptions}
                    value={name}
                    onChange={handleOnSearchCustomer}
                    onSelectOption={handleOnSelectCustomer}
                    noOptionsMessage={!name ? 'Search for customer name or ID' : 'No customer found'}
                    isLoading={gettingCustomers}
                    autoComplete='off'
                    optionSelected={!!customerOptions.find((option) => option.label === formik.values.name)}
                    inputTestId='conversion-customer-name-input-dropdown'
                    optionTestId='conversion-customer-name-options-list-dropdown'
                />

                <LabelledInput
                    id='name'
                    name='name'
                    label='Customer Name* :'
                    value={formik.values.name}
                    error={formik.touched.name ? formik.errors.name : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                    className={FormStyles['span-2']}
                />

                <LabelledInput
                    id='idNo'
                    name='idNo'
                    label='ID Number* :'
                    value={formik.values.idNo}
                    error={formik.touched.idNo ? formik.errors.idNo : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                />

                <LabelledInput
                    id='CIFNo'
                    name='CIFNo'
                    label='CIF No.* :'
                    value={formik.values.CIFNo}
                    error={formik.submitCount > 0 && formik.touched.CIFNo ? formik.errors.CIFNo : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                />

                <LabelledDropdown
                    id='branchName'
                    name='branchName'
                    label='Branch*: '
                    options={branchOptions}
                    value={formik.values.branchName}
                    error={formik.touched.branchName ? formik.errors.branchName : undefined}
                    onChange={handleOnSearchBranch}
                    onSelectOption={handleOnSelectBranch}
                    // onBlur={formik.handleBlur}
                    noOptionsMessage='No branches found'
                    autoComplete='off'
                    optionSelected={!!branchOptions.find((branch) => branch.label === formik.values.branchName)}
                    disabled={!allBranchAccess}
                />

                <LabelledInput
                    id='branchCode'
                    name='branchCode'
                    label='Branch Code* :'
                    value={formik.values.branchCode}
                    error={formik.touched.branchCode ? formik.errors.branchCode : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                />

                <LabelledDropdown
                    id='region'
                    name='region'
                    label='Region* :'
                    options={regionOptions}
                    value={formik.values.region}
                    error={formik.submitCount > 0 && formik.touched.region ? formik.errors.region : undefined}
                    onChange={handleOnSearchRegion}
                    onSelectOption={handleOnSelectRegion}
                    onBlur={formik.handleBlur}
                    noOptionsMessage='No Region found'
                    className={FormStyles['span-2']}
                    autoComplete='off'
                    optionSelected={!!regionOptions.find((option) => option.label === formik.values.region)}
                    disabled
                />

                <LabelledDropdown
                    id='metalType'
                    name='metalType'
                    label='Metal Type *:'
                    options={metalTypeOptions}
                    value={
                        metalTypeOptions.find(
                            (option) => option.value === formik.values.metalType
                        )?.label ?? ''
                    }
                    error={
                        formik.touched.metalType && formik.errors.metalType
                            ? formik.errors.metalType
                            : undefined
                    }
                    onSelectOption={(option) => {
                        const selectedMetalType = Number(option.value) as MetalTypeEnum;

                        formik.setFieldValue('metalType', option.value);

                        const paperProduct = customerInventory.find(item =>
                            selectedMetalType === MetalTypeEnum.Silver
                                ? item.silverType === SilverTypeEnum.PaperSilver
                                : item.goldType === GoldTypeEnum.PaperGold
                        );
                        formik.setFieldValue('originProductId', paperProduct?.productId?.toString() || '');

                        dispatch(combinedActions.goldSetInventoryFilterParams({
                            ...inventoryFilterParams,
                            branchCode: formik.values.branchCode,
                            metalType: selectedMetalType,
                            goldType: selectedMetalType === MetalTypeEnum.Gold ? GoldTypeEnum.PhysicalGold : undefined,
                            silverType: selectedMetalType === MetalTypeEnum.Silver ? SilverTypeEnum.PhysicalSilver : undefined,
                        }));
                        dispatch(combinedActions.goldGetAllInventoriesAttempt());
                    }}
                    // onBlur={formik.handleBlur}
                    optionSelected={!!formik.values.metalType}
                    noOptionsMessage='No metal type found'
                />

                <div />

                <LabelledDropdown
                    id={'targetProductId'}
                    name={'targetProductId'}
                    label='Convert To* :'
                    options={physicalGoldSilverProducts.sort((a, b) => a.value - b.value)}
                    value={targetProductName || ''}
                    error={formik.touched.targetProductId ? formik.errors.targetProductId : undefined}
                    onChange={(e) => handleOnSearchConvertToProduct(e)}
                    onSelectOption={(e) => handleOnSelectConvertToProduct(e)}
                    // onBlur={formik.handleBlur}
                    noOptionsMessage='No product found'
                    autoComplete='off'
                    optionSelected={!!physicalGoldSilverProducts.find((product) => product.label === targetProductName)}
                    disabled={!formik.values.branchCode}
                />

                <div className={FormStyles['available-balance']}>
                    <Text
                        label='Available balance:'
                    />

                    <Text
                        label={`${fixedDecimal(
                            Number(formik.values.metalType) === MetalTypeEnum.Silver
                                ? formik.values.availablePaperSilver
                                : formik.values.availablePaperGold,
                            3
                        )} g`}
                        isBold
                    />
                </div>

                <LabelledInput
                    data-testid='create-gold-conversion-quantity-input'
                    id={'quantity'}
                    name={'quantity'}
                    label='Quantity*: '
                    value={formik.values.quantity}
                    error={formik.touched.quantity ? formik.errors.quantity : undefined}
                    onChange={handleOnChangeQuantity}
                    onBlur={handleQuantityOnBlur}
                />

                <LabelledInput
                    data-testid='create-gold-conversion-serialNo-input'
                    id={'serialNo'}
                    name={'serialNo'}
                    label='Serial Number (comma delimited)* :'
                    value={formik.values.serialNo}
                    error={formik.touched.serialNo ? formik.errors.serialNo : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                />

                <LabelledInput
                    id={'salesPersonnelName'}
                    name={'salesPersonnelName'}
                    label='Sales Personnel Name:'
                    value={formik.values.salesPersonnelName}
                    error={formik.touched.salesPersonnelName ? formik.errors.salesPersonnelName : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                />

                <LabelledInput
                    id={'referralName'}
                    name={'referralName'}
                    label='Referral Name :'
                    value={formik.values.referralName}
                    error={formik.touched.referralName ? formik.errors.referralName : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                />

            </div>

            <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                <div className={FormStyles['button-subcontainer']}>
                    <ActionButton
                        testId='create-conversion-submit-button'
                        label='Create Conversion'
                        type='submit'
                        isLoading={createInventoryIsLoading}
                        disabled={createInventoryIsLoading}
                    />
                </div>
            </div>
        </form>
    );
};

export default CreateGoldConversionForm;
